import type { ApiFailure } from './types'

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message)
  }
}

export const CSRF_COOKIE = 'sc_csrf'

function cookie(name: string): string | undefined {
  return document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`))
    ?.slice(name.length + 1)
}

/** Fetch `/api/v1{path}` as JSON, sending the CSRF header on mutations. */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')
  const method = (init.method ?? 'GET').toUpperCase()
  const csrf = cookie(CSRF_COOKIE)
  if (csrf && !['GET', 'HEAD'].includes(method)) headers.set('x-csrf-token', decodeURIComponent(csrf))
  const response = await fetch(`/api/v1${path}`, { ...init, headers, credentials: 'same-origin' })
  if (!response.ok) {
    let body: ApiFailure | undefined
    try {
      body = (await response.json()) as ApiFailure
    } catch {
      // Not JSON: a proxy or network error page.
    }
    throw new ApiError(response.status, body?.error.code ?? 'request_failed', body?.error.message ?? `Request failed (${response.status}).`)
  }
  if (response.status === 204) return undefined as T
  return response.json() as Promise<T>
}

export function searchParams(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') params.set(key, String(value))
  }
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function json(body: unknown): string {
  return JSON.stringify(body)
}
